// (B) Radix Popover 백엔드 — antd Popover 대체. antd DOM 미러링:
// .om-popover(Content, rootStyle) > .om-popover-inner > .om-popover-inner-content(bodyStyle, content) / .om-popover-arrow.
// Radix Popover 는 Provider 불필요. 시각은 styles/omwPopover.css(antd v5 파리티).
//
// ⚠ 실측 소비처(calendar·EventDetail 6곳)는 전부 controlled `open`(상시 오픈)+trigger="click" → open 을 Radix 에 그대로 전달.
//   getPopupContainer 는 Radix 가 앵커(floating-ui)로 위치를 잡으므로 위치엔 무관 → body Portal 로 통일(클리핑 회피).
//   트리거는 asChild ref 안전을 위해 .om-popover-trigger span 으로 감싼다.
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { cloneElement, isValidElement, type CSSProperties, type ReactElement } from 'react'
import type { OmwPopoverPlacement, OmwPopoverProps } from '../api/OmwPopover'
import { cx } from './cx'
import '../styles/omwPopover.css'

// Radix Trigger(asChild)는 트리거 ARIA(aria-haspopup="dialog"/aria-expanded)를 "대상 요소"에 병합하고,
// 그 요소의 rect 가 floating-ui 의 위치 계산 앵커가 된다. 따라서:
//  - host 요소(string type: button/a/div/span…): 자식에 직접 병합(cloneElement) → antd 처럼 자식 DOM
//    자체가 앵커가 되어, position:absolute/fixed 좌표 지정 트리거(calendar 이벤트 상세 등)가 정확히 동작.
//    비인터랙티브 host 에는 role="button"+tabIndex 를 병합해 axe `aria-allowed-attr`(critical) 위반 회피.
//    `.om-popover-trigger` 클래스는 자식으로 이관해 테스트 파리티 유지(display 오버라이드 없음 = antd 파리티).
//  - 그 외(문자열·컴포넌트): span 래퍼에 role="button"+tabIndex 부여(키보드 접근성 확보). 래퍼가 앵커가
//    되므로 좌표 지정 트리거는 반드시 host 요소로 넘길 것.
const INTERACTIVE_HOST = new Set(['button', 'a'])

type SideAlign = { side: 'top' | 'bottom' | 'left' | 'right'; align: 'start' | 'center' | 'end' }

const PLACEMENT: Record<OmwPopoverPlacement, SideAlign> = {
  top: { side: 'top', align: 'center' },
  topLeft: { side: 'top', align: 'start' },
  topRight: { side: 'top', align: 'end' },
  bottom: { side: 'bottom', align: 'center' },
  bottomLeft: { side: 'bottom', align: 'start' },
  bottomRight: { side: 'bottom', align: 'end' },
  left: { side: 'left', align: 'center' },
  right: { side: 'right', align: 'center' },
}

export function PlainPopover({
  content,
  open,
  onOpenChange,
  placement = 'top',
  bodyStyle,
  rootStyle,
  arrow,
  className,
  children,
}: OmwPopoverProps) {
  const { side, align } = PLACEMENT[placement] ?? PLACEMENT.top
  const rootCss: CSSProperties = {
    zIndex: 'var(--om-z-dropdown)' as unknown as number,
    ...rootStyle,
  }

  const childIsHostElement =
    isValidElement(children) && typeof (children as ReactElement).type === 'string'

  const triggerNode = childIsHostElement ? (
    cloneElement(
      children as ReactElement<{ className?: string; role?: string; tabIndex?: number }>,
      {
        className: cx(
          'om-popover-trigger',
          (children as ReactElement<{ className?: string }>).props.className,
        ),
        ...(INTERACTIVE_HOST.has((children as ReactElement).type as string)
          ? {}
          : { role: 'button', tabIndex: 0 }),
      },
    )
  ) : (
    <span className="om-popover-trigger" role="button" tabIndex={0}>
      {children}
    </span>
  )

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{triggerNode}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={cx('om-popover', className)}
          aria-label="Popover"
          side={side}
          align={align}
          sideOffset={4}
          style={rootCss}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="om-popover-inner">
            <div className="om-popover-inner-content" style={bodyStyle}>
              {content}
            </div>
          </div>
          {arrow !== false && (
            <PopoverPrimitive.Arrow className="om-popover-arrow" width={16} height={8} />
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
